// ---------------------------------------------------------------------------
// Accessify – Text Simplifier client-side IndexedDB cache.
// ---------------------------------------------------------------------------
// Third cache layer (after KV manifest and live AI). Persists per-block
// simplified text in the browser so SPA navigation and rapid toggle-on/off
// do not re-fetch from the worker.
//
// Keys are `${pageUrl}:simplify:${level}:${blockHash}` so different
// simplify levels (einfache vs. leichte) don't collide on the same page.
// Failures (private mode, disabled storage, quota) are silent and the
// caller falls back to the network path.
// ---------------------------------------------------------------------------

import type { SimplifyLevel } from './text-simplify-types';

const CLIENT_CACHE_DB = 'accessify-simplify-cache';
const CLIENT_CACHE_STORE = 'blocks';
const CLIENT_CACHE_VERSION = 1;

function openClientCache(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(CLIENT_CACHE_DB, CLIENT_CACHE_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CLIENT_CACHE_STORE)) {
        db.createObjectStore(CLIENT_CACHE_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export interface IdbCache {
  /** Compose the storage key for a given block hash on the current page. */
  key(blockHash: string): string;
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
  /** Clear every cached entry for the current page+level — used after a
   *  fresh server manifest arrives so we don't replay stale live-AI
   *  hallucinations on top of authoritative server data. */
  clearForCurrentPage(): Promise<void>;
}

export function createIdbCache(level: SimplifyLevel): IdbCache {
  const pageKeyBase = () =>
    `${window.location.origin}${window.location.pathname}:simplify:${level}:`;

  return {
    key(blockHash) {
      return pageKeyBase() + blockHash;
    },

    async get(key) {
      try {
        const db = await openClientCache();
        return new Promise((resolve) => {
          const tx = db.transaction(CLIENT_CACHE_STORE, 'readonly');
          const store = tx.objectStore(CLIENT_CACHE_STORE);
          const req = store.get(key);
          req.onsuccess = () => resolve(req.result ?? null);
          req.onerror = () => resolve(null);
        });
      } catch {
        return null;
      }
    },

    async set(key, value) {
      try {
        const db = await openClientCache();
        const tx = db.transaction(CLIENT_CACHE_STORE, 'readwrite');
        tx.objectStore(CLIENT_CACHE_STORE).put(value, key);
      } catch {
        /* private mode / quota / no IDB — best-effort */
      }
    },

    async delete(key) {
      try {
        const db = await openClientCache();
        const tx = db.transaction(CLIENT_CACHE_STORE, 'readwrite');
        tx.objectStore(CLIENT_CACHE_STORE).delete(key);
      } catch {
        /* best-effort */
      }
    },

    async clearForCurrentPage() {
      try {
        const db = await openClientCache();
        const tx = db.transaction(CLIENT_CACHE_STORE, 'readwrite');
        const store = tx.objectStore(CLIENT_CACHE_STORE);
        const prefix = pageKeyBase();

        // IDBObjectStore has no native prefix delete — cursor-iterate.
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return;
          if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) {
            cursor.delete();
          }
          cursor.continue();
        };
      } catch {
        /* best-effort */
      }
    },
  };
}
